import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, ShoppingCart, Search, Camera, ChevronDown, Sparkles, Flame } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import UserMenu from '@/components/UserMenu';
import { productThumb } from '@/lib/imageOptim';
import { publicData } from '@/lib/publicData';
const SearchByImageModal = lazy(() => import('@/components/SearchByImageModal'));
import axios from 'axios';

const LOGO_LIGHT = '/assets/ogmini-logo-light-v1.webp';
const LOGO_DARK = '/assets/ogmini-logo-dark-v1.webp';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_LINKS = [
  { label: 'Brands', to: '/brands' },
  { label: 'Category', to: '/categories' },
  {
    label: 'Scale',
    type: 'dropdown',
    items: [
      { label: '1:18', to: '/shop?scale=1:18' },
      { label: '1:24', to: '/shop?scale=1:24' },
      { label: '1:32', to: '/shop?scale=1:32' },
      { label: '1:36', to: '/shop?scale=1:36' },
      { label: '1:43', to: '/shop?scale=1:43' },
      { label: '1:64', to: '/shop?scale=1:64' },
    ],
  },
  {
    label: 'Type',
    type: 'dropdown',
    items: [
      { label: 'Diecast Cars',           to: '/shop?type=diecast_car', icon: '🚗' },
      { label: 'RC Models',              to: '/shop?type=rc',          icon: '📡', tag: 'NEW' },
      { label: 'Bikes',                  to: '/shop?type=bike',        icon: '🏍️' },
      { label: 'Trucks',                 to: '/shop?type=truck',       icon: '🚚' },
      { label: 'Airplanes',              to: '/shop?type=airplane',    icon: '✈️' },
      { label: 'Military',               to: '/shop?type=military',    icon: '🪖' },
      { label: 'Construction Machinery', to: '/shop?type=construction',icon: '🚜' },
    ],
  },
  { label: 'Pre-Orders', to: '/pre-orders' },
];

function useScrollToSection() {
  const location = useLocation();
  const navigate = useNavigate();
  return (target) => {
    const scrollTo = () => {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (location.pathname !== '/') {
      // Navigate home first, then scroll after render
      navigate('/');
      setTimeout(scrollTo, 200);
    } else {
      scrollTo();
    }
  };
}

function NavSearch({ scrolled }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const debounceRef = useRef(null);
  const suggestionRequest = useRef(null);
  useEffect(() => () => { clearTimeout(debounceRef.current); suggestionRequest.current?.abort(); }, []);
  const navigate = useNavigate();
  // Image search is only reliable on the shop page right now — the camera
  // affordance is intentionally scoped there per product spec. Everywhere
  // else (home, brands, etc.) we keep the plain text-search input.
  const { pathname } = useLocation();
  const showCamera = pathname.startsWith('/shop');

  const fetchSuggestions = async (q) => {
    if (q.length < 2) { setSuggestions([]); return; }
    try {
      suggestionRequest.current?.abort();
      const controller = new AbortController();
      suggestionRequest.current = controller;
      const { data } = await axios.get(`${API}/products?search=${encodeURIComponent(q)}&limit=5&summary=true&include_facets=false`, { signal: controller.signal });
      setSuggestions(data.products || []);
    } catch (error) { if (!axios.isCancel(error)) setSuggestions([]); }
  };

  const handleChange = (e) => {
    suggestionRequest.current?.abort();
    setSuggestions([]);
    setQuery(e.target.value);
    setShowSuggestions(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(e.target.value), 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setShowSuggestions(false);
      navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
      setQuery('');
    }
  };

  return (
    <div className="relative hidden md:block w-36 lg:w-48">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${scrolled ? 'text-gray-400' : 'text-white/40'}`} />
          <input value={query} onChange={handleChange} onFocus={() => query.length >= 2 && setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Search…" data-testid="nav-search"
            className={`w-full text-[12px] pl-8 ${showCamera ? 'pr-8' : 'pr-2.5'} py-1.5 rounded-md outline-none transition-all ${scrolled ? 'bg-gray-100 border border-gray-200 text-[#1A1A1A] placeholder:text-gray-400 focus:border-[#FF2A2A]/50' : 'bg-white/10 border border-white/10 text-white placeholder:text-white/40 focus:border-white/30'}`} />
          {showCamera && (
            <button type="button" onClick={() => setImageOpen(true)}
              data-testid="nav-image-search-btn" title="Search by photo"
              className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors ${scrolled ? 'text-gray-400 hover:text-[#FF2A2A] hover:bg-gray-200' : 'text-white/50 hover:text-white hover:bg-white/10'}`}>
              <Camera size={12} />
            </button>
          )}
        </div>
      </form>
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {suggestions.map(p => (
            <button key={p.id} onMouseDown={() => { setShowSuggestions(false); setQuery(''); navigate(`/product/${p.id}`); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left">
              <div className="w-9 h-9 bg-gray-100 rounded flex-shrink-0 flex items-center justify-center">
                {p.images?.[0] && <img src={productThumb(p.images[0], 'thumb')} alt={p.name} width="36" height="36" className="max-h-[28px] object-contain" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-[#1A1A1A] truncate">{p.name}</p>
                <p className="text-[9px] text-gray-400">{p.brand} - ₹ {p.price?.toLocaleString()}</p>
              </div>
            </button>
          ))}
          <button onMouseDown={handleSubmit} className="w-full text-center py-2 text-[10px] font-bold text-[#FF2A2A] bg-gray-50 hover:bg-gray-100 border-t border-gray-100">
            See all results
          </button>
        </div>
      )}
      {imageOpen && <Suspense fallback={null}><SearchByImageModal
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        API={API}
        onSelect={(p) => { setImageOpen(false); navigate(`/product/${p.id}`); }}
      /></Suspense>}
    </div>
  );
}

function MobileSearchTrigger({ scrolled }) {
  // Tapping the icon opens a full-screen search overlay with a focused
  // text input + live suggestions. Camera button inside also gives access
  // to image search — but ONLY on /shop, where the image-search results
  // are reliable. On the home page the camera would show but the results
  // page doesn't exist yet, so we hide it to avoid the reported glitch.
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [imageOpen, setImageOpen] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionRequest = useRef(null);
  useEffect(() => () => { clearTimeout(debounceRef.current); suggestionRequest.current?.abort(); }, []);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const showCamera = pathname.startsWith('/shop');

  useEffect(() => {
    if (open) {
      // Brief delay so the slide-in animation settles before focus —
      // otherwise iOS Safari sometimes scrolls weirdly.
      setTimeout(() => inputRef.current?.focus(), 120);
    } else {
      setQuery(''); setSuggestions([]);
    }
  }, [open]);

  const fetchSuggestions = async (q) => {
    if (q.length < 2) { setSuggestions([]); return; }
    try {
      suggestionRequest.current?.abort();
      const controller = new AbortController();
      suggestionRequest.current = controller;
      const { data } = await axios.get(`${API}/products?search=${encodeURIComponent(q)}&limit=6&summary=true&include_facets=false`, { signal: controller.signal });
      setSuggestions(data.products || []);
    } catch (error) { if (!axios.isCancel(error)) setSuggestions([]); }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    suggestionRequest.current?.abort();
    setSuggestions([]);
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setOpen(false);
      navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="mobile-search-trigger"
        title="Search"
        aria-label="Search"
        className={`md:hidden p-2 rounded-lg transition-colors ${scrolled ? 'text-gray-500 hover:text-[#1A1A1A] hover:bg-gray-100' : 'text-white/80 hover:text-white hover:bg-white/10'}`}>
        <Search size={20} />
      </button>

      <>
        {open && (
          <div
            className="md:hidden fixed inset-0 z-[60] bg-white flex flex-col"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
            data-testid="mobile-search-overlay">
            {/* Header bar — taller now so the search field is properly
                reachable with one thumb. */}
            <div
              className="border-b border-gray-100 px-3 py-3 flex items-center gap-2 flex-shrink-0">
              <button onClick={() => setOpen(false)} aria-label="Close search"
                data-testid="mobile-search-close"
                className="p-3 -ml-1 text-gray-500 hover:text-[#1A1A1A]">
                <X size={22} />
              </button>
              <form onSubmit={handleSubmit} className="flex-1">
                <div className="relative">
                  <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={inputRef}
                    value={query} onChange={handleChange}
                    placeholder="Search models, brands…"
                    data-testid="mobile-search-input"
                    autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                    enterKeyHint="search"
                    className={`w-full bg-gray-100 border border-gray-200 text-[#1A1A1A] text-base pl-11 ${showCamera ? 'pr-14' : 'pr-3'} py-3.5 rounded-xl outline-none focus:border-[#FF2A2A]/50 focus:bg-white placeholder:text-gray-400`} />
                  {showCamera && (
                    <button type="button" onClick={() => setImageOpen(true)}
                      data-testid="mobile-search-image-btn"
                      title="Search by photo"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2.5 rounded-lg text-gray-500 hover:text-[#FF2A2A] active:bg-gray-200">
                      <Camera size={18} />
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Live suggestions / empty state — uses flex-1 + overflow
                so the soft keyboard doesn't push the whole screen out
                of view. */}
            <div className="px-3 py-2 overflow-y-auto flex-1 -webkit-overflow-scrolling-touch" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}>
              {query.length < 2 ? (
                <div className="py-14 text-center">
                  <Search size={32} className="mx-auto mb-3 text-gray-300" />
                  <p className="text-sm text-gray-400">Start typing to search products…</p>
                  <button onClick={() => { setOpen(false); navigate('/shop'); }}
                    data-testid="mobile-search-browse-all"
                    className="mt-5 text-xs font-bold tracking-wider uppercase text-[#FF2A2A] active:text-[#FF4D4D] py-2 px-3">
                    Or browse all products →
                  </button>
                </div>
              ) : suggestions.length === 0 ? (
                <p className="text-sm text-gray-400 py-12 text-center">No matches for "{query}"</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {suggestions.map(p => (
                    <li key={p.id}>
                      <button onClick={() => { setOpen(false); navigate(`/product/${p.id}`); }}
                        data-testid={`mobile-search-result-${p.id}`}
                        className="w-full flex items-center gap-3 py-3.5 px-1 text-left active:bg-gray-50 rounded-lg">
                        <div className="w-14 h-14 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
                          {p.images?.[0] && <img src={p.images[0]} alt="" className="max-h-[48px] object-contain" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-[#1A1A1A] line-clamp-2 leading-snug">{p.name}</p>
                          <p className="text-[10px] font-bold tracking-wider uppercase text-gray-500 mt-0.5">{p.brand}{p.category ? ` · ${p.category}` : ''}</p>
                        </div>
                        <span className="text-sm font-bold text-[#FF2A2A] flex-shrink-0">₹{p.price?.toLocaleString()}</span>
                      </button>
                    </li>
                  ))}
                  <li>
                    <button onClick={handleSubmit}
                      data-testid="mobile-search-see-all"
                      className="w-full text-center py-4 text-xs font-bold uppercase tracking-wider text-[#FF2A2A]">
                      See all results for "{query}" →
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        )}
      </>

      {imageOpen && <Suspense fallback={null}><SearchByImageModal
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        API={API}
        onSelect={(p) => { setImageOpen(false); setOpen(false); navigate(`/product/${p.id}`); }}
      /></Suspense>}
    </>
  );
}



/**
 * Hover/click-to-open dropdown for "Scale" / "Shop by Type" nav links.
 * Hovering opens it (with a 200ms close grace so the cursor can travel
 * to the panel without flicker). Tapping the label opens it on mobile
 * if/when we render this in a touch context. Items are plain <Link>s so
 * router navigation + browser back-button stay snappy.
 */
function NavDropdown({ link, scrolled }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  return (
    <div className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-dropdown-${link.label.toLowerCase().replace(/\s/g, '-')}`}
        className={`flex items-center gap-1 text-[11px] lg:text-xs font-semibold tracking-[0.14em] uppercase whitespace-nowrap transition-colors duration-300 ${scrolled ? 'text-gray-500 hover:text-[#1A1A1A]' : 'text-white/70 hover:text-white'}`}
      >
        {link.label}
        <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <>
        {open && (
          <div
            data-testid={`nav-dropdown-panel-${link.label.toLowerCase().replace(/\s/g, '-')}`}
            className="absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[220px] bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden z-50"
          >
            {link.items.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-dropdown-item-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#FF2A2A] hover:text-white transition-colors"
              >
                <span className="flex items-center gap-2">
                  {item.icon && <span className="text-base leading-none">{item.icon}</span>}
                  <span className="font-semibold tracking-wide">{item.label}</span>
                </span>
                {item.tag && (
                  <span className="text-[9px] font-bold tracking-wider uppercase bg-[#FF2A2A] text-white px-1.5 py-0.5 rounded">
                    {item.tag}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </>
    </div>
  );
}


export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { itemCount, cartBounce, lastAdded } = useCart();
  const scrollToSection = useScrollToSection();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/90 md:backdrop-blur-xl border-b border-gray-200 shadow-sm' : 'bg-[#050505]/80 md:backdrop-blur-xl'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <a href="/" data-testid="nav-logo" onClick={(e) => { e.preventDefault(); scrollToSection('hero'); }} className="flex items-center cursor-pointer">
            <img data-testid="navigation-logo-image" src={scrolled ? LOGO_DARK : LOGO_LIGHT} alt="OGMini" loading="eager" decoding="async" width="144" height="48" className="h-12 w-36 object-contain" />
        </a>

        <div className="hidden lg:flex items-center gap-5 lg:gap-6">
          {NAV_LINKS.map(link => {
            if (link.type === 'dropdown') {
              return <NavDropdown key={link.label} link={link} scrolled={scrolled} />;
            }
            return link.to ? (
              <Link
                key={link.label}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-[11px] lg:text-xs font-semibold tracking-[0.14em] uppercase whitespace-nowrap transition-colors duration-300 cursor-pointer ${link.highlight ? 'text-[#FF2A2A] hover:text-[#FF4D4D]' : scrolled ? 'text-gray-500 hover:text-[#1A1A1A]' : 'text-white/70 hover:text-white'}`}>
                {link.label}
              </Link>
            ) : (
              <button
                key={link.label}
                onClick={() => scrollToSection(link.target)}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-[11px] lg:text-xs font-semibold tracking-[0.14em] uppercase whitespace-nowrap transition-colors duration-300 cursor-pointer ${link.highlight ? 'text-[#FF2A2A] hover:text-[#FF4D4D]' : scrolled ? 'text-gray-500 hover:text-[#1A1A1A]' : 'text-white/70 hover:text-white'}`}>
                {link.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <NavSearch scrolled={scrolled} />
          {/* Mobile-only quick search trigger — opens the image-search modal directly
              (text search is reachable via /shop). Keeps the camera affordance
              on every page without crowding the nav. */}
          <MobileSearchTrigger scrolled={scrolled} />
          <Link to="/cart" data-testid="nav-cart-link" className={`relative p-2 transition-all ${cartBounce ? 'scale-125' : ''} ${scrolled ? 'text-gray-500 hover:text-[#1A1A1A]' : 'text-white/70 hover:text-white'}`} style={{ transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
            <ShoppingCart size={20} />
            {itemCount > 0 && (
              <span className={`absolute -top-0.5 -right-0.5 bg-[#FF2A2A] text-white text-[9px] font-bold w-5 h-5 rounded-full flex items-center justify-center transition-transform ${cartBounce ? 'scale-150' : 'scale-100'}`} style={{ transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
                {itemCount}
              </span>
            )}
          </Link>
          <UserMenu variant={scrolled ? 'light' : 'dark'} />
          <DealsCta />
          <button data-testid="mobile-menu-toggle" className={`lg:hidden p-2 ${scrolled ? 'text-[#1A1A1A]' : 'text-white'}`}
            onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <>
        {mobileOpen && (
          <div data-testid="mobile-menu"
            className="lg:hidden bg-white/95 md:backdrop-blur-xl border-b border-gray-200 overflow-hidden">
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV_LINKS.map(link => {
                if (link.type === 'dropdown') {
                  return (
                    <div key={link.label} className="flex flex-col gap-2">
                      <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-gray-400">{link.label}</p>
                      <div className="flex flex-wrap gap-2">
                        {link.items.map((item) => (
                          <Link
                            key={item.label}
                            to={item.to}
                            onClick={() => setMobileOpen(false)}
                            data-testid={`mobile-nav-item-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                            className="inline-flex items-center gap-1.5 text-sm font-bold tracking-wide bg-gray-100 text-[#1A1A1A] px-3 py-1.5 rounded-lg hover:bg-[#FF2A2A] hover:text-white transition-colors"
                          >
                            {item.icon && <span>{item.icon}</span>}
                            {item.label}
                            {item.tag && <span className="text-[8px] font-bold uppercase bg-[#FF2A2A] text-white px-1 rounded ml-1">{item.tag}</span>}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }
                return link.to ? (
                  <Link key={link.label} to={link.to} data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`} onClick={() => setMobileOpen(false)}
                    className="text-lg font-semibold tracking-[0.15em] uppercase text-gray-500 hover:text-[#1A1A1A] transition-colors text-left">{link.label}</Link>
                ) : (
                  <button key={link.label} data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`} onClick={() => { setMobileOpen(false); scrollToSection(link.target); }}
                    className="text-lg font-semibold tracking-[0.15em] uppercase text-gray-500 hover:text-[#1A1A1A] transition-colors text-left">{link.label}</button>
                );
              })}
              <Link to="/shop" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-all-products-btn"
                className="flex items-center justify-center gap-2 bg-[#FF2A2A] text-white font-bold uppercase tracking-wider text-sm px-6 py-4 mt-2">
                <ShoppingBag size={16} /> All Products
              </Link>
            </div>
          </div>
        )}
      </>
    </nav>
  );
}


/**
 * Header "Deals" quick-action CTA.
 *
 * Replaces the previous Pre-Order dropdown (Pre-Orders is now a proper
 * main-nav item) with a single-tap Deals shortcut. Keeps the highlighted
 * button slot on the right side of the header so shoppers always have a
 * one-click path to today's discounts.
 */
function DealsCta() {
  return (
    <Link
      to="/deals"
      data-testid="deals-cta-btn"
      className="hidden md:inline-flex items-center gap-2 bg-[#FF2A2A] text-white font-bold uppercase tracking-wider text-sm px-6 py-3 border border-[#FF2A2A] hover:bg-white hover:text-[#FF2A2A] transition-all duration-300"
    >
      <Flame size={16} /> Deals
    </Link>
  );
}


/**
 * Header "Pre-Order ▾" mega-dropdown.
 *
 * Hover (desktop) / tap (touch) reveals every brand that currently has
 * an active pre-order SKU — sourced from `/api/meta/preorder-brands`
 * so it's always in sync with what the admin uploaded. Each row links
 * to `/shop?preorder=true&brand=<name>` so customers can jump straight
 * into the right brand's pre-order shelf.
 *
 * Falls back to a plain "Pre-Order" link to the unfiltered preorder
 * view if the list is empty or the fetch fails.
 */
function PreOrderDropdown() {
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await publicData('meta/preorder-brands');
        if (!cancelled) setBrands(data?.brands || []);
      } catch (e) { /* fall back to single-link mode */ }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="hidden md:block relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link to="/shop?preorder=true" data-testid="preorder-cta-btn"
        onClick={() => setOpen(false)}
        className="flex items-center gap-2 bg-amber-400 text-[#1A1A1A] font-bold uppercase tracking-wider text-sm px-6 py-3 border border-amber-400 hover:bg-transparent hover:text-amber-400 transition-all duration-300">
        <Sparkles size={16} /> Pre-Order
        {brands.length > 0 && <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />}
      </Link>
      {open && brands.length > 0 && (
        <div data-testid="preorder-brand-dropdown"
          className="absolute right-0 top-full mt-1 w-64 bg-white shadow-2xl border border-gray-100 rounded-lg overflow-hidden z-50">
          <div className="px-4 py-2 bg-amber-50 text-[10px] font-bold tracking-wider uppercase text-amber-700 border-b border-amber-100">
            Pre-order by brand
          </div>
          <div className="max-h-80 overflow-y-auto">
            {brands.map(b => (
              <Link key={b.name} to={`/shop?preorder=true&brand=${encodeURIComponent(b.name)}`}
                data-testid={`preorder-brand-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-amber-50 hover:text-amber-800 transition-colors border-b border-gray-50 last:border-b-0">
                <span className="font-semibold">{b.name} Pre-order</span>
                <span className="text-[10px] text-gray-400">{b.count}</span>
              </Link>
            ))}
          </div>
          <Link to="/shop?preorder=true" onClick={() => setOpen(false)}
            className="block text-center text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A] bg-gray-50 hover:bg-amber-100 py-2.5 border-t border-gray-100">
            See all pre-orders →
          </Link>
        </div>
      )}
    </div>
  );
}
